import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** sm: ~400px / md: ~520px / lg: ~720px */
  size?: 'sm' | 'md' | 'lg';
  /**
   * When `false`, the modal cannot be dismissed via ESC, backdrop click, or the
   * close icon — caller-owned actions inside the modal body must drive `onClose`.
   * Used by the Phase 7-dashboard-b profile-completion gate where the candidate
   * MUST either save or sign out. Defaults to `true`.
   */
  dismissible?: boolean;
}

const sizeClasses: Record<NonNullable<Props['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-3xl',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
}: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus management: run ONLY on the open-transition, not on every render.
  // Depending on `onClose`/`dismissible` here would re-fire this effect whenever the parent
  // re-renders (e.g. on every keystroke into a TextArea inside the modal), which would
  // refocus the dialog div and steal focus from the input the user is typing into.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => {
      previouslyFocused?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && dismissible) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, dismissible]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-black/40"
            onClick={dismissible ? onClose : undefined}
            aria-hidden
          />
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal
            aria-labelledby="modal-title"
            tabIndex={-1}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className={`relative bg-white rounded-2xl shadow-2xl border border-gray-100 w-full ${sizeClasses[size]} max-h-[90vh] flex flex-col overflow-hidden focus:outline-none`}
          >
            <div className="flex-shrink-0 flex items-start justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 id="modal-title" className="text-lg font-display font-bold text-brand-primary">
                  {title}
                </h2>
                {description && <p className="text-sm text-brand-muted mt-1">{description}</p>}
              </div>
              {dismissible && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="p-1 -mr-1 text-gray-400 hover:text-brand-primary transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
            {footer && <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">{footer}</div>}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
